import React from "react";
import { Route, Navigate } from "react-router-dom";
import { MainLayout } from "@/layout";
import PrivateRoute from "@/layout/PrivateRoute";

// Lazy-loaded page components
const Index = React.lazy(() => import("@/pages/Index"));
const Login = React.lazy(() => import("@/pages/Login"));
const StoreInformation = React.lazy(() => import("@/pages/StoreInformation"));
const Menu = React.lazy(() => import("@/components/menu/MenuPage"));
const Deals = React.lazy(() => import("@/pages/Deals"));
const PizzaDetails = React.lazy(
  () => import("@/components/PizzaDetails/PizzaDetailsPage"),
);
const ChickenDipDetails = React.lazy(
  () => import("@/components/ChickenDipDetails/ChickenDipDetailsPage"),
);
const DealDetail = React.lazy(
  () => import("@/components/DealDetail/DealDetailPage"),
);
const Basket = React.lazy(() => import("@/pages/Basket"));
const Checkout = React.lazy(() => import("@/pages/Checkout"));
const PaymentPage = React.lazy(() => import("@/pages/PaymentPage"));
const PaymentFormIframe = React.lazy(() => import("@/pages/PaymentFormIframe"));
const PaymentCallback = React.lazy(() => import("@/pages/PaymentCallback"));
const PaymentFailed = React.lazy(() => import("@/pages/PaymentFailed"));
const OrderConfirmation = React.lazy(() => import("@/pages/OrderConfirmation"));
const TrackOrder = React.lazy(() => import("@/pages/TrackOrder"));
const ContactForm = React.lazy(() => import("@/pages/ContactForm"));
const ContactUs = React.lazy(() => import("@/pages/ContactUs"));
const NotFound = React.lazy(() => import("@/pages/NotFound"));
const Error500 = React.lazy(() => import("@/pages/Error500"));
const VisionPurposeValuesPage = React.lazy(
  () => import("@/pages/Vision-purpose-values"),
);

// Legal pages
const TermsAndConditionsOfSale = React.lazy(
  () => import("@/pages/TermsAndConditionsOfSale"),
);
const TermsOfUse = React.lazy(() => import("@/pages/TermsOfUse"));
const PrivacyPolicy = React.lazy(() => import("@/pages/PrivacyPolicy"));
const CopyrightAndLegal = React.lazy(() => import("@/pages/CopyrightAndLegal"));
const JointControllerStatement = React.lazy(
  () => import("@/pages/Jointcontrollerstatement"),
);
const BorigLegalStuff = React.lazy(() => import("@/pages/BorigLegalStuff"));

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <React.Suspense fallback={<div>Loading...</div>}>{children}</React.Suspense>
  );
}

export function MainRoutes() {
  return (
    <>
      {/* Public auth routes */}
      <Route
        path="/login"
        element={
          <MainLayout>
            <SuspenseWrapper>
              <Login />
            </SuspenseWrapper>
          </MainLayout>
        }
      />
      <Route path="/register" element={<Navigate to="/login" replace />} />

      {/* Card payment form iframe - public, served from same domain */}
      <Route
        path="/payment/form-iframe"
        element={
          <SuspenseWrapper>
            <PaymentFormIframe />
          </SuspenseWrapper>
        }
      />

      {/* 3DS Challenge callback - public, loaded inside iframe by ACS */}
      <Route
        path="/payment/callback"
        element={
          <SuspenseWrapper>
            <PaymentCallback />
          </SuspenseWrapper>
        }
      />

      {/* Protected routes */}
      <Route element={<PrivateRoute />}>
        <Route element={<MainLayout />}>
          <Route
            path="/"
            element={
              <SuspenseWrapper>
                <Index />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/store-information"
            element={
              <SuspenseWrapper>
                <StoreInformation />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/menu"
            element={
              <SuspenseWrapper>
                <Menu />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/deals"
            element={
              <SuspenseWrapper>
                <Deals />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/pizza-details"
            element={
              <SuspenseWrapper>
                <PizzaDetails />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/chicken-dip-details"
            element={
              <SuspenseWrapper>
                <ChickenDipDetails />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/deal-detail"
            element={
              <SuspenseWrapper>
                <DealDetail />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/basket"
            element={
              <SuspenseWrapper>
                <Basket />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/checkout"
            element={
              <SuspenseWrapper>
                <Checkout />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/payment"
            element={
              <SuspenseWrapper>
                <PaymentPage />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/payment-failed"
            element={
              <SuspenseWrapper>
                <PaymentFailed />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/order-confirmation"
            element={
              <SuspenseWrapper>
                <OrderConfirmation />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/track-order"
            element={
              <SuspenseWrapper>
                <TrackOrder />
              </SuspenseWrapper>
            }
          />
          {/* Legal pages */}
          <Route
            path="/terms-and-conditions-of-sale"
            element={
              <SuspenseWrapper>
                <TermsAndConditionsOfSale />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/terms-of-use"
            element={
              <SuspenseWrapper>
                <TermsOfUse />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <SuspenseWrapper>
                <PrivacyPolicy />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/copyright-and-legal"
            element={
              <SuspenseWrapper>
                <CopyrightAndLegal />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/jointcontrollerstatement"
            element={
              <SuspenseWrapper>
                <JointControllerStatement />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/boring-legal-stuff"
            element={
              <SuspenseWrapper>
                <BorigLegalStuff />
              </SuspenseWrapper>
            }
          />

          {/* Other pages */}
          <Route
            path="/contact-us"
            element={
              <SuspenseWrapper>
                <ContactUs />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/contact-form"
            element={
              <SuspenseWrapper>
                <ContactForm />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/vision-purpose-values-legacy"
            element={
              <SuspenseWrapper>
                <VisionPurposeValuesPage />
              </SuspenseWrapper>
            }
          />
          <Route
            path="/500"
            element={
              <SuspenseWrapper>
                <Error500 />
              </SuspenseWrapper>
            }
          />
          <Route
            path="*"
            element={
              <SuspenseWrapper>
                <NotFound />
              </SuspenseWrapper>
            }
          />
        </Route>
      </Route>
    </>
  );
}
